import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Focus, Check } from 'lucide-react';
import { queryKeys, type LaunchFocus } from '@vtak/shared';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { workspaceClient } from '@/services/workspace-client';
import { useUpcomingLaunches, usePreviousLaunches, useLaunchLocations } from '@/queries/use-launch-queries';
import { distinctValues } from '@/components/launch/filter-launches';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

/**
 * Admin editor for the unit's Launch Focus — which launch sites and rockets the
 * Launch Dashboard highlights by default. Rocket options are derived from live
 * launch data (never hardcoded); saving PATCHes the unit profile.
 */
export function LaunchFocusEditor({ unitId }: { unitId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const savedFocus = useWorkspaceStore((s) => s.workspace?.launchFocus);
  const [sites, setSites] = useState<string[]>(savedFocus?.sites ?? []);
  const [rockets, setRockets] = useState<string[]>(savedFocus?.rockets ?? []);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upcoming = useUpcomingLaunches(true);
  const previous = usePreviousLaunches(true);
  const locations = useLaunchLocations();

  const allLaunches = useMemo(
    () => [...(upcoming.data ?? []), ...(previous.data ?? [])],
    [upcoming.data, previous.data],
  );
  const rocketOptions = useMemo(() => uniqueRockets(allLaunches), [allLaunches]);
  // Union of the static focus-site catalog, distinct locations seen in live
  // launch data, and anything already saved (so saved chips never vanish).
  const siteOptions = useMemo(() => {
    const sites = new Set<string>((locations.data ?? []).map((location) => location.name));
    for (const location of distinctValues(allLaunches, (l) => l.location)) sites.add(location);
    for (const saved of savedFocus?.sites ?? []) sites.add(saved);
    return [...sites].sort((a, b) => a.localeCompare(b));
  }, [locations.data, allLaunches, savedFocus]);

  const dirty =
    !sameSet(sites, savedFocus?.sites ?? []) || !sameSet(rockets, savedFocus?.rockets ?? []);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const launchFocus: LaunchFocus = { sites, rockets };
      await workspaceClient.updateProfile(unitId, { launchFocus });
      useWorkspaceStore.setState((state) => ({
        workspace: state.workspace ? { ...state.workspace, launchFocus } : state.workspace,
      }));
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.config(unitId) });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch {
      setError('Save failed — check your permissions and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScrollArea className="flex-1">
      <div className="space-y-4 p-3">
        <p className="flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground">
          <Focus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-echo-yellow" />
          Choose the launch sites and rockets this unit focuses on. The Launch
          Dashboard opens in the focused view by default.
        </p>

        <ChipGroup
          label="Sites"
          hint="Empty = all sites"
          options={siteOptions}
          selected={sites}
          onToggle={(v) => setSites(toggle(sites, v))}
        />

        <ChipGroup
          label="Rockets"
          hint={rocketOptions.length > 0 ? 'Empty = all rockets' : 'Waiting for launch data…'}
          options={rocketOptions}
          selected={rockets}
          onToggle={(v) => setRockets(toggle(rockets, v))}
        />

        {error && <p className="text-[11px] text-red-400">{error}</p>}

        <button
          onClick={save}
          disabled={!dirty || saving}
          className={cn(
            'flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-all',
            savedFlash
              ? 'bg-green-500/20 text-green-400'
              : dirty
                ? 'bg-echo-yellow text-black hover:bg-echo-yellow/90'
                : 'cursor-default bg-white/[0.06] text-muted-foreground',
          )}
        >
          {savedFlash ? (
            <>
              <Check className="h-3.5 w-3.5" /> Saved
            </>
          ) : saving ? (
            'Saving…'
          ) : (
            'Save focus'
          )}
        </button>
      </div>
    </ScrollArea>
  );
}

/** A labelled group of toggleable selection pills. */
function ChipGroup({
  label,
  hint,
  options,
  selected,
  onToggle,
}: {
  label: string;
  hint: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}): React.JSX.Element {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <p className="font-chakra text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          {label}
        </p>
        <p className="text-[9px] text-muted-foreground/70">{hint}</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const active = selected.includes(option);
          return (
            <button
              key={option}
              onClick={() => onToggle(option)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                active
                  ? 'border-echo-yellow/60 bg-echo-yellow/15 text-echo-yellow'
                  : 'border-echo-border text-muted-foreground hover:border-white/25 hover:text-foreground',
              )}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Unique base rocket names from launches, sorted (e.g. "Falcon 9 Block 5" → "Falcon 9 Block 5"). */
function uniqueRockets(launches: Array<{ rocket: string }>): string[] {
  const names = new Set<string>();
  for (const launch of launches) {
    if (launch.rocket) names.add(launch.rocket);
  }
  return [...names].sort();
}

/** Toggle a value in a string list. */
function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** Order-insensitive equality of two string lists. */
function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b);
  return a.every((v) => set.has(v));
}
